import React, { useEffect, useState } from "react";
import { Table, Pagination, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import Typography from "style-guide/component/Typography";
import Button from "style-guide/component/Button";
import { AppDispatch, RootState } from "redux/store";
import { useSelector, useDispatch } from "react-redux";
import { Invoice } from "types/subscription";
import { getInvoices } from "../../../redux/slice/rfpModuleSlice/subscription";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

const InvoicesTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingInvoices, setDownloadingInvoices] = useState<Set<string>>(new Set());
  const dispatch = useDispatch<AppDispatch>();
  const { invoiceTableData } = useSelector((state: RootState) => state.subscriptionData);
  const { isLoadingModal } = useSelector((state: RootState) => state.applicationData);

  useEffect(() => {
    dispatch(getInvoices(currentPage));
  }, [dispatch, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    if (!invoice.invoicePdf) {
      message.error("PDF URL not available for this invoice");
      return;
    }

    // Add invoice to downloading set
    setDownloadingInvoices(prev => new Set(prev).add(invoice.id));

    try {
      window.open(invoice.invoicePdf, "_blank", "noopener,noreferrer");
    } catch (error) {
      message.error("Failed to download invoice. Please try again.");
    } finally {
      // Remove invoice from downloading set
      setDownloadingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
    }
  };

  const handleRetryPayment = (invoice: Invoice) => {
    // TODO: Implement retry payment logic
    message.info(`Retry payment for invoice ${invoice.id}`);
  };

  const columns: ColumnsType<Invoice> = [
    {
      title: "Date",
      dataIndex: "created",
      key: "date",
      minWidth: 300,
      sorter: (a, b) =>
        new Date(a.created.split(".").reverse().join("-")).getTime() -
        new Date(b.created.split(".").reverse().join("-")).getTime(),
      render: (created: string) => <Typography variant="Base" text={created} />,
    },
    {
      title: "Invoice ID",
      dataIndex: "id",
      key: "id",
      minWidth: 300,
      render: (invoiceId: string) => (
        <Typography variant="Base" text={invoiceId} />
      ),
    },
    {
      title: "Amount",
      dataIndex: "total",
      key: "amount",
      minWidth: 300,
      sorter: (a, b) => a.total - b.total,
      render: (total: number) => (
        <Typography variant="Base" text={`$${total.toFixed(2)}`} />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      minWidth: 300,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status: "Paid" | "failed") => (
        <span
          className={`inline-flex px-3 py-1 rounded-md text-sm font-medium capitalize ${
            status?.toLowerCase() === "paid"
              ? "bg-green-100 text-[#237804] border-2 border-green-300"
              : "bg-red-100 text-red-800 border-2 border-red-300"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      minWidth: 300,
      render: (_, record) => {
        const isDownloading = downloadingInvoices.has(record.id);
        
        return record.status?.toLowerCase() === "paid" ? (
          <Button
            type={isDownloading ? "Disabled" : "Default"}
            buttonText={isDownloading ? "Downloading..." : "Download Invoice"}
            size="Small"
            onClick={isDownloading ? undefined : () => handleDownloadInvoice(record)}
            loading={isDownloading}
            className={
              isDownloading 
                ? "opacity-50 cursor-not-allowed" 
                : "text-blue-primary border-blue-primary hover:bg-blue-50"
            }
            iconComponent={
              isDownloading ? (
                <ReloadOutlined className="animate-spin" />
              ) : (
                <DownloadOutlined />
              )
            }
          />
        ) : (
          <Button
            type="Default"
            buttonText="Retry Payment"
            size="Small"
            onClick={() => handleRetryPayment(record)}
            className="text-blue-primary border-blue-primary hover:bg-blue-50"
            iconComponent={<ReloadOutlined />}
          />
        );
      },
    },
  ];

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="Heading 4" text="Invoice History" />
      </div>

      {/* Ant Design Table */}
      <div className="bg-white overflow-hidden">
        <Table
          columns={columns}
          dataSource={invoiceTableData?.data}
          rowKey="id"
          pagination={false}
          className="invoice-table"
          size="middle"
          scroll={{ x: "max-content" }}
          loading={isLoadingModal}
        />

        {/* Ant Design Pagination */}
        <div className="px-6 py-4 bg-white">
          <div className="flex justify-end">
            <Pagination
              current={currentPage}
              total={invoiceTableData?.pagination?.total}
              onChange={handlePageChange}
              showSizeChanger={false}
              hideOnSinglePage
            />
          </div>
        </div>
      </div>

      {/* Custom styles for the table */}
      <style>
        {`
          .invoice-table .ant-table-thead > tr > th {
            background-color: #f9f9f9 !important;
          }
        `}
      </style>
    </div>
  );
};

export default InvoicesTable;
